/**
 * features/dashboard/DashboardStudentSearch.tsx
 * ------------------------------------------------------------------
 * Universal search panel — finds students, teachers, and classes
 * from a single search box on the dashboard. Results are categorized
 * with icons so the user knows what they're clicking.
 * ------------------------------------------------------------------
 */
import { useMemo, useState } from 'react';
import {
  Search,
  X,
  ArrowRight,
  GraduationCap,
  Users,
  BookOpen,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { canViewStudentProfile } from '@/lib/permissions';
import { Card } from '@/components/ui/primitives';
import StudentAvatar from '@/components/ui/StudentAvatar';
import type { TabId } from '@/components/Shell';
import type { Student } from '@/types';

interface Props {
  onNavigate: (t: TabId, opts?: { studentId?: string }) => void;
}

type ResultType = 'student' | 'teacher' | 'class';

interface UniversalResult {
  type: ResultType;
  id: string;
  primary: string;
  secondary: string;
  extra?: string;
  student?: Student;
}

export default function DashboardStudentSearch({ onNavigate }: Props) {
  const { userData } = useAuth();
  const { students: allStudents, classes, teachers } = useData();
  // Exclude 'pending' (Pastel-only, not yet enrolled) from search + counts.
  const students = allStudents.filter((s) => s.status !== 'pending');
  const [q, setQ] = useState('');

  const canView = useMemo(
    () => (s: Student) => canViewStudentProfile(userData, s, teachers),
    [userData, teachers],
  );

  const results = useMemo(() => {
    const query = (q ?? '').trim().toLowerCase();
    if (query.length === 0) return [];

    const out: UniversalResult[] = [];
    const limit = 12;

    // Search students
    for (const s of students) {
      if (out.length >= limit) break;
      const name = (s.fullName ?? '').toLowerCase();
      const adm = (s.admissionNumber ?? '').toLowerCase();
      if ((name.includes(query) || (adm.length > 0 && adm.includes(query))) && canView(s)) {
        const cls = classes.find((c) => c.id === s.currentClass);
        out.push({
          type: 'student',
          id: s.id,
          primary: s.fullName,
          secondary: cls?.name ?? s.currentClass,
          extra: adm.includes(query) ? s.admissionNumber : undefined,
          student: s,
        });
      }
    }

    // Search teachers
    for (const t of teachers) {
      if (out.length >= limit) break;
      const name = (t.fullName ?? '').toLowerCase();
      const display = (t.displayName ?? '').toLowerCase();
      const email = (t.email ?? '').toLowerCase();
      const dept = (t.department ?? '').toLowerCase();
      if (name.includes(query) || display.includes(query) || email.includes(query) || dept.includes(query)) {
        out.push({
          type: 'teacher',
          id: t.id,
          primary: `${t.title} ${t.fullName}`,
          secondary: t.department ?? 'No department',
          extra: email.includes(query) ? t.email : undefined,
        });
      }
    }

    // Search classes
    for (const c of classes) {
      if (out.length >= limit) break;
      const name = (c.name ?? '').toLowerCase();
      if (name.includes(query)) {
        const count = students.filter((s) => s.currentClass === c.id).length;
        const ct = teachers.find((t) => t.classTeacherOf === c.id);
        out.push({
          type: 'class',
          id: c.id,
          primary: c.name,
          secondary: `${count} students`,
          extra: ct ? `CT: ${ct.displayName}` : undefined,
        });
      }
    }

    // Sort: exact matches first, then by type (students, teachers, classes)
    const typeOrder: Record<ResultType, number> = { student: 0, teacher: 1, class: 2 };
    out.sort((a, b) => {
      const aExact = a.primary.toLowerCase() === query ? 0 : 1;
      const bExact = b.primary.toLowerCase() === query ? 0 : 1;
      if (aExact !== bExact) return aExact - bExact;
      return typeOrder[a.type] - typeOrder[b.type];
    });

    return out;
  }, [q, students, teachers, classes, canView]);

  const queried = q.trim().length > 0;

  function handleClick(r: UniversalResult) {
    switch (r.type) {
      case 'student':
        onNavigate('studentProfile', { studentId: r.id });
        break;
      case 'teacher':
        onNavigate('teacherProfile', { studentId: r.id });
        break;
      case 'class':
        // Navigate to students filtered by class — for now just go to students
        onNavigate('students');
        break;
    }
    setQ('');
  }

  const typeIcon = (type: ResultType) => {
    switch (type) {
      case 'student': return <GraduationCap size={14} className="text-primary" />;
      case 'teacher': return <Users size={14} className="text-indigo-500" />;
      case 'class': return <BookOpen size={14} className="text-emerald-500" />;
    }
  };

  const typeLabel = (type: ResultType) => {
    switch (type) {
      case 'student': return 'Student';
      case 'teacher': return 'Teacher';
      case 'class': return 'Class';
    }
  };

  return (
    <Card className="p-4 mb-6">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Search size={16} />
        </div>
        <div className="flex-1 relative">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search students, teachers, or classes…"
            aria-label="Search"
            className="w-full bg-transparent text-sm focus:outline-none placeholder:text-gray-400"
          />
          {q && (
            <button
              onClick={() => setQ('')}
              aria-label="Clear search"
              className="absolute right-0 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {queried && (
        <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-3">
          {results.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-2">
              No results for "{q.trim()}".
            </p>
          ) : (
            <ul className="space-y-1">
              {results.map((r) => (
                <li key={`${r.type}-${r.id}`}>
                  <button
                    onClick={() => handleClick(r)}
                    className="w-full text-left flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-primary/5 group transition-colors"
                  >
                    {/* Avatar or icon */}
                    {r.type === 'student' && r.student ? (
                      <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0">
                        <StudentAvatar student={r.student} size="sm" />
                      </div>
                    ) : (
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        r.type === 'teacher' ? 'bg-indigo-50 dark:bg-indigo-900/30' : 'bg-emerald-50 dark:bg-emerald-900/30'
                      }`}>
                        {typeIcon(r.type)}
                      </div>
                    )}

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm text-gray-800 dark:text-white truncate">
                          {r.primary}
                        </p>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                          r.type === 'student' ? 'bg-primary/10 text-primary' :
                          r.type === 'teacher' ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300' :
                          'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300'
                        }`}>
                          {typeLabel(r.type)}
                        </span>
                      </div>
                      <div className="flex items-center gap-x-3 text-xs text-gray-500 flex-wrap">
                        <span>{r.secondary}</span>
                        {r.extra && (
                          <span className="text-gray-400">{r.extra}</span>
                        )}
                      </div>
                    </div>

                    <ArrowRight
                      size={14}
                      className="text-gray-300 group-hover:text-primary shrink-0"
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}
